import { FadeUp, SectionTag } from "../components/site/Reveal";

function LegalShell({ tag, title, updated, children, testId }) {
  return (
    <div data-testid={testId} className="bg-navy pt-16 min-h-screen blueprint-grid">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24">
        <FadeUp>
          <SectionTag dark>{tag}</SectionTag>
          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl mt-6">{title}</h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-4">[Last updated: {updated}]</p>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-12 space-y-8 text-slate-300 text-sm leading-relaxed
          [&_h2]:font-display [&_h2]:text-white [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3
          [&_p]:leading-relaxed">
          {children}
        </FadeUp>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell tag="Legal // Privacy" title="Privacy Policy" updated="July 2026" testId="privacy-page">
      <section><h2>What we collect</h2>
        <p>When you apply, contact us, or enroll, we collect the information you provide: name, email, phone, business name, trade, and operational details you share in your application. WorkBase Hub stores your module progress and support requests.</p></section>
      <section><h2>How we use it</h2>
        <p>To review applications, deliver the program, send transactional email (application updates, sign-in codes, enrollment confirmations), process payment through our payment processor, and support you as a member. We do not sell your information.</p></section>
      <section><h2>Payments</h2>
        <p>Payments are processed by Stripe. We never see or store your full card number; Stripe's own privacy policy governs payment data.</p></section>
      <section><h2>Security & retention</h2>
        <p>Access is passwordless (one-time email codes), and member data is role-restricted. We keep application and membership records while your account is active; email us to request deletion.</p></section>
      <section><h2>Contact</h2>
        <p>Questions about this policy? Reach us through the contact page.</p></section>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell tag="Legal // Terms" title="Terms of Service" updated="July 2026" testId="terms-page">
      <section><h2>The service</h2>
        <p>WorkBase Systems provides The Back Office Blueprint, a guided implementation program: training, templates, workflows, coaching, and accountability for building back-office systems. It is educational and advisory — not legal, tax, or accounting advice.</p></section>
      <section><h2>Your responsibilities</h2>
        <p>You are responsible for implementing systems in your business, for the accuracy of your application information, and for keeping your sign-in email accessible. Outcomes depend on your implementation.</p></section>
      <section><h2>Accounts & access</h2>
        <p>WorkBase Hub access is granted to accepted, enrolled members and is personal to you. Don't share access. We may suspend access for abuse or chargebacks outside the refund policy.</p></section>
      <section><h2>Intellectual property</h2>
        <p>Templates and materials are licensed to you for use in your own business. Don't resell or redistribute them.</p></section>
      <section><h2>Limitation of liability</h2>
        <p>To the maximum extent permitted by law, our liability is limited to the amount you paid for the program.</p></section>
    </LegalShell>
  );
}

export function Refunds() {
  return (
    <LegalShell tag="Legal // Refunds" title="Refund Policy" updated="July 2026" testId="refunds-page">
      <section><h2>14-day fit guarantee</h2>
        <p>If within 14 days of your cohort start date you decide The Back Office Blueprint isn't right for you, contact us and we'll refund your enrollment in full — no forms, no friction.</p></section>
      <section><h2>After 14 days</h2>
        <p>Because the program delivers digital materials and live coaching from week one, enrollments are non-refundable after the 14-day window, except where required by law.</p></section>
      <section><h2>How to request</h2>
        <p>Message us from the contact page or reply to any program email from your enrolled address. Refunds are issued to your original payment method, typically within 5–10 business days.</p></section>
    </LegalShell>
  );
}

export function ProgramAgreement() {
  return (
    <LegalShell tag="Legal // Enrollment" title="Program Agreement" updated="July 2026" testId="agreement-page">
      <section><h2>Scope</h2>
        <p>The Back Office Blueprint is a 7-week guided implementation program covering lead capture, estimates, scheduling, customer records, invoicing, payments, and follow-up. It includes training, templates, workflows, group coaching calls, and accountability check-ins.</p></section>
      <section><h2>Guided, not done-for-you</h2>
        <p>We do not perform implementation for you. You build each system with our guidance. Results depend on your participation and implementation.</p></section>
      <section><h2>Enrollment</h2>
        <p>Enrollment requires an accepted application and completed payment — Founding Cohort pricing is $497 paid in full or 2 monthly payments of $275. Access to WorkBase Hub is granted after payment confirmation.</p></section>
      <section><h2>Fair use</h2>
        <p>Materials are for your business only. Coaching calls may be recorded and shared with enrolled members. Abuse, harassment, or redistribution ends access without refund.</p></section>
      <section><h2>Guarantee</h2>
        <p>The 14-day fit guarantee in our Refund Policy applies to this agreement.</p></section>
    </LegalShell>
  );
}
